import React, { useEffect, useRef, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router";

import {
  CacheForm,
  type CacheFormHandle,
} from "@domains/resource/components/CreateResourceDrawer/CacheForm/CacheForm";
import type { CacheFormState } from "@domains/resource/components/CreateResourceDrawer/CacheForm/useCacheForm";
import {
  DatabaseForm,
  type DatabaseFormHandle,
} from "@domains/resource/components/CreateResourceDrawer/DatabaseForm/DatabaseForm";
import type { DatabaseFormState } from "@domains/resource/components/CreateResourceDrawer/DatabaseForm/useDatabaseForm";
import {
  QueueForm,
  type QueueFormHandle,
} from "@domains/resource/components/CreateResourceDrawer/QueueForm/QueueForm";
import type { QueueFormState } from "@domains/resource/components/CreateResourceDrawer/QueueForm/useQueueForm";
import { useInfraConfig } from "@domains/resource/hooks/useInfraConfig";
import { useCreateResource } from "@domains/resource/hooks/useResources";
import type {
  ManagedResourceUI,
  ResourceCreateType,
  CreateDatabaseRequest,
  CreateCacheRequest,
  CreateQueueRequest,
} from "@domains/resource/types";
import { ORGANIZATIONS, PROJECTS } from "src/platform/constants/routes";
import {
  getContainerStyles,
  sectionLabelSx,
} from "src/shell/styles/themeUtils";

interface ResourcePreview {
  name: string;
  modeLabel: string;
  clusterLabel: string;
}

const RESOURCE_META: Record<
  ResourceCreateType,
  {
    title: string;
    typeLabel: string;
    accessLabel: string;
    connPrefix: string;
    connHost: string;
  }
> = {
  db: {
    title: "Provision Database",
    typeLabel: "PostgreSQL",
    accessLabel: "PgBouncer pool",
    connPrefix: "postgresql://",
    connHost: "rusha-pooler:5432",
  },
  cache: {
    title: "Provision Cache",
    typeLabel: "Redis",
    accessLabel: "Direct connection",
    connPrefix: "redis://",
    connHost: "rusha-redis:6379",
  },
  queue: {
    title: "Provision Queue",
    typeLabel: "RabbitMQ / NATS / Kafka / Pulsar",
    accessLabel: "AMQP / NATS / Kafka protocol",
    connPrefix: "amqp://",
    connHost: "rusha-broker:5672",
  },
};

function isResourceType(v: string): v is ResourceCreateType {
  return v === "db" || v === "cache" || v === "queue";
}

export function ProvisionResourcePage(): React.ReactElement {
  const {
    orgAlias,
    projectAlias,
    resourceType: rtParam,
  } = useParams<{
    orgAlias: string;
    projectAlias: string;
    resourceType: string;
  }>();
  const theme = useTheme();

  const resourceType = isResourceType(rtParam ?? "")
    ? (rtParam as ResourceCreateType)
    : "db";
  const meta = RESOURCE_META[resourceType];

  const dbRef = useRef<DatabaseFormHandle>(null);
  const cacheRef = useRef<CacheFormHandle>(null);
  const queueRef = useRef<QueueFormHandle>(null);
  const {
    createDatabase,
    createCache,
    createQueue,
    isSubmitting,
    submitError,
  } = useCreateResource();
  const {
    infraConfig,
    loading: infraLoading,
    error: infraError,
    reload,
  } = useInfraConfig(projectAlias);

  const [successResource, setSuccessResource] =
    useState<ManagedResourceUI | null>(null);

  // Live preview state — polled from form refs
  const [preview, setPreview] = useState<ResourcePreview>({
    name: "",
    modeLabel: "Shared",
    clusterLabel: "Rusha CNPG",
  });

  useEffect(() => {
    if (successResource) return;
    const id = window.setInterval(() => {
      if (resourceType === "db") {
        const s: DatabaseFormState | undefined = dbRef.current?.getState();
        if (s) {
          setPreview({
            name: s.name || "my_database",
            modeLabel: s.mode === "shared" ? "Shared" : "Dedicated",
            clusterLabel: s.mode === "shared" ? "Rusha CNPG" : "Dedicated CNPG",
          });
        }
      } else if (resourceType === "cache") {
        const s: CacheFormState | undefined = cacheRef.current?.getState();
        if (s) {
          setPreview({
            name: s.name || "my_cache",
            modeLabel: "Dedicated",
            clusterLabel: "Dedicated Redis",
          });
        }
      } else if (resourceType === "queue") {
        const s: QueueFormState | undefined = queueRef.current?.getState();
        if (s) {
          setPreview({
            name: s.name || "my_queue",
            modeLabel: s.mode === "shared" ? "Shared" : "Dedicated",
            clusterLabel:
              s.queueType === "rabbitmq"
                ? "RabbitMQ cluster"
                : s.queueType === "nats"
                  ? "NATS cluster"
                  : s.queueType === "kafka"
                    ? "Kafka cluster"
                    : "Pulsar cluster",
          });
        }
      }
    }, 200);
    return () => window.clearInterval(id);
  }, [resourceType, successResource]);

  const projectPath = projectAlias
    ? PROJECTS.DETAILS.getPath(orgAlias ?? "", projectAlias)
    : ORGANIZATIONS.ROOT.path;

  const handleProvision = () => {
    if (resourceType === "db") {
      dbRef.current?.submit();
    } else if (resourceType === "cache") {
      cacheRef.current?.submit();
    } else if (resourceType === "queue") {
      queueRef.current?.submit();
    }
  };

  const handleDbSubmit = async (req: CreateDatabaseRequest) => {
    const result = await createDatabase(projectAlias ?? "", req);
    if (result) setSuccessResource(result);
  };

  const handleCacheSubmit = async (req: CreateCacheRequest) => {
    const result = await createCache(projectAlias ?? "", req);
    if (result) setSuccessResource(result);
  };

  const handleQueueSubmit = async (req: CreateQueueRequest) => {
    const result = await createQueue(projectAlias ?? "", req);
    if (result) setSuccessResource(result);
  };

  if (infraLoading) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <Stack spacing={2} sx={{ py: 4 }}>
          <Typography variant="h5" fontWeight={700}>
            Checking infrastructure setup
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We&apos;ll show the resource form after confirming where this
            project runs.
          </Typography>
        </Stack>
      </Container>
    );
  }

  if (infraError || !infraConfig?.cloudProvider) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <Stack spacing={2.5} sx={{ py: 4, maxWidth: 680 }}>
          <Typography variant="h5" fontWeight={700}>
            Set up infrastructure before creating a resource
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Infrastructure is the project execution target where Rusha runs
            databases, caches, queues, and applications. Choose Rusha Hosted or
            connect a cloud provider from the project Overview before
            provisioning this resource.
          </Typography>
          {infraError ? (
            <Alert severity="error">
              We could not check this project&apos;s infrastructure setup.
              <Button
                size="small"
                onClick={() => void reload()}
                sx={{ ml: 1, textTransform: "none" }}
              >
                Retry
              </Button>
            </Alert>
          ) : (
            <Alert severity="warning">
              No infrastructure is configured for this project yet. Resource
              creation is paused until a project target is selected.
            </Alert>
          )}
          <Button
            component={RouterLink}
            to={projectPath}
            variant="contained"
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            Set up infrastructure from project Overview
          </Button>
        </Stack>
      </Container>
    );
  }

  // ─── Success state ───────────────────────────────────────────────
  if (successResource) {
    return (
      <Container sx={getContainerStyles(theme, "lg")}>
        <Stack spacing={3} sx={{ py: 4 }}>
          {/* Success header */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.success.main, 0.12),
                color: "success.main",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Resource provisioned successfully
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {successResource.name} is being provisioned
              </Typography>
            </Box>
          </Stack>

          <Divider />

          {/* Details card */}
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            }}
          >
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 2 }}>
              RESOURCE DETAILS
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {successResource.name}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {meta.typeLabel}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Mode
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {successResource.provisionMode === "shared"
                    ? "Shared"
                    : "Dedicated"}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Alias
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  fontFamily="monospace"
                >
                  {successResource.alias}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Provisioning note */}
          <Alert severity="warning" variant="outlined">
            Provisioning in progress. This usually takes 30-60 seconds.
            You&apos;ll be notified when it&apos;s ready.
          </Alert>

          {/* Actions */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              component={RouterLink}
              to={projectPath}
              variant="outlined"
              sx={{ textTransform: "none" }}
            >
              Add another resource
            </Button>
            <Button
              component={RouterLink}
              to={projectPath}
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              Back to project
            </Button>
          </Stack>
        </Stack>
      </Container>
    );
  }

  // ─── Form state ──────────────────────────────────────────────────
  return (
    <Container sx={getContainerStyles(theme, "lg")}>
      <Stack spacing={3} sx={{ py: 3 }}>
        {/* Back link */}
        <Link
          component={RouterLink}
          to={projectPath}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            textDecoration: "none",
            color: "text.secondary",
            "&:hover": { color: "primary.main" },
          }}
        >
          <ArrowBackIcon fontSize="small" />
          <Typography variant="body2">Back to project</Typography>
        </Link>

        {/* Page header */}
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {meta.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {projectAlias} · Rusha Hosted · eu-central-1
          </Typography>
        </Box>

        <Divider />

        {/* Two-column layout: form + preview */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="flex-start"
        >
          {/* Left: Form */}
          <Box sx={{ flex: 1, maxWidth: 600 }}>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}

            {resourceType === "db" && (
              <DatabaseForm
                ref={dbRef}
                projectAlias={projectAlias ?? ""}
                onSubmit={handleDbSubmit}
                isSubmitting={isSubmitting}
              />
            )}
            {resourceType === "cache" && (
              <CacheForm
                ref={cacheRef}
                onSubmit={handleCacheSubmit}
                isSubmitting={isSubmitting}
              />
            )}
            {resourceType === "queue" && (
              <QueueForm
                ref={queueRef}
                onSubmit={handleQueueSubmit}
                isSubmitting={isSubmitting}
              />
            )}
          </Box>

          {/* Right: Preview panel */}
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              position: "sticky",
              top: 24,
            }}
          >
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1 }}>
              PREVIEW
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              {preview.name}
            </Typography>

            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {meta.typeLabel}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Mode
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {preview.modeLabel}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Cluster
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {preview.clusterLabel}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Access
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {meta.accessLabel}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Region
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  eu-central-1
                </Typography>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Connection string
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.common.black, 0.2),
                fontFamily: "monospace",
                fontSize: "0.7rem",
                lineHeight: 1.6,
              }}
            >
              <Box component="span" sx={{ color: "success.main" }}>
                {meta.connPrefix}
              </Box>
              <br />
              <Box component="span" sx={{ color: "text.secondary" }}>
                {"  ****:****@"}
              </Box>
              <br />
              <Box component="span" sx={{ color: "text.secondary" }}>
                {`  ${meta.connHost}`}
              </Box>
            </Box>
          </Box>
        </Stack>

        {/* Sticky footer */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            pt: 2,
            pb: 1,
            bgcolor: "background.paper",
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <Button
            component={RouterLink}
            to={projectPath}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleProvision}
            disabled={isSubmitting}
            sx={{ textTransform: "none", fontWeight: 600, minWidth: 160 }}
          >
            {isSubmitting ? "Provisioning…" : meta.title}
          </Button>
        </Box>
      </Stack>
    </Container>
  );
}
